import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Toastify } from "../../components/Toaster";

export interface OAuthDynamicClientSchema {
  ClientId?: string;
  ClientName?: string;
  RegistrationType?: string;
  ClientUri?: string;
  RedirectURIs?: string[];
  Scope?: string[];
  Metadata?: Record<string, any>;
  JwksUri?: string;
  Jwks?: any;
  IsActive?: boolean;
  CreatedAt?: string;
  UpdatedAt?: string;
  [key: string]: any;
}

export interface OAuthConsentedUserSchema {
  Uid?: string;
  ClientId?: string;
  ClientName?: string;
  ClientAuthentications?: any[];
  [key: string]: any;
}

export interface OAuthClientsState {
  clients: OAuthDynamicClientSchema[];
  clientsLoading: boolean;
  clientsError: string | null;
  selectedClient: OAuthDynamicClientSchema | null;

  clientUsers: OAuthConsentedUserSchema[];
  clientUsersLoading: boolean;
  clientUsersError: string | null;

  deletingClient: boolean;
  revokingUser: boolean;
}

const initialState: OAuthClientsState = {
  clients: [],
  clientsLoading: false,
  clientsError: null,
  selectedClient: null,

  clientUsers: [],
  clientUsersLoading: false,
  clientUsersError: null,

  deletingClient: false,
  revokingUser: false
};

const oauthClientsSlice = createSlice({
  name: "oauthClients",
  initialState,
  reducers: {
    fetchOAuthClientsRequest(state, _action: PayloadAction<string>) {
      state.clientsLoading = true;
      state.clientsError = null;
    },
    fetchOAuthClientsSuccess(state, action: PayloadAction<OAuthDynamicClientSchema[]>) {
      state.clientsLoading = false;
      state.clients = action.payload;
    },
    fetchOAuthClientsFailure(state, action: PayloadAction<string>) {
      state.clientsLoading = false;
      state.clientsError = action.payload;
      Toastify("error", action.payload);
    },

    setSelectedOAuthClient(state, action: PayloadAction<OAuthDynamicClientSchema | null>) {
      state.selectedClient = action.payload;
      state.clientUsers = [];
      state.clientUsersError = null;
      state.clientUsersLoading = false;
    },

    fetchOAuthClientUsersRequest(state, _action: PayloadAction<{ serverId: string; clientId: string }>) {
      state.clientUsersLoading = true;
      state.clientUsersError = null;
    },
    fetchOAuthClientUsersSuccess(state, action: PayloadAction<OAuthConsentedUserSchema[]>) {
      state.clientUsersLoading = false;
      state.clientUsers = action.payload;
    },
    fetchOAuthClientUsersFailure(state, action: PayloadAction<string>) {
      state.clientUsersLoading = false;
      state.clientUsersError = action.payload;
      Toastify("error", action.payload);
    },

    deleteOAuthClientRequest(state, _action: PayloadAction<{ serverId: string; clientId: string }>) {
      state.deletingClient = true;
    },
    deleteOAuthClientSuccess(state, action: PayloadAction<string>) {
      state.deletingClient = false;
      state.clients = state.clients.filter((c) => c.ClientId !== action.payload);
      if (state.selectedClient?.ClientId === action.payload) {
        state.selectedClient = null;
        state.clientUsers = [];
      }
    },
    deleteOAuthClientFailure(state, action: PayloadAction<string>) {
      state.deletingClient = false;
      Toastify("error", action.payload);
    },

    revokeOAuthClientUserRequest(state, _action: PayloadAction<{ serverId: string; clientId: string; uid: string }>) {
      state.revokingUser = true;
    },
    revokeOAuthClientUserSuccess(state, action: PayloadAction<{ uid: string }>) {
      state.revokingUser = false;
      state.clientUsers = state.clientUsers.filter((u) => u.Uid !== action.payload.uid);
    },
    revokeOAuthClientUserFailure(state, action: PayloadAction<string>) {
      state.revokingUser = false;
      Toastify("error", action.payload);
    },

    resetOAuthClientsState() {
      return initialState;
    }
  }
});

export const {
  fetchOAuthClientsRequest,
  fetchOAuthClientsSuccess,
  fetchOAuthClientsFailure,
  setSelectedOAuthClient,
  fetchOAuthClientUsersRequest,
  fetchOAuthClientUsersSuccess,
  fetchOAuthClientUsersFailure,
  deleteOAuthClientRequest,
  deleteOAuthClientSuccess,
  deleteOAuthClientFailure,
  revokeOAuthClientUserRequest,
  revokeOAuthClientUserSuccess,
  revokeOAuthClientUserFailure,
  resetOAuthClientsState
} = oauthClientsSlice.actions;

export default oauthClientsSlice.reducer;
